import { ChartField } from '../chart.model'
import type { ItemValueFunc } from 'src/app/shared/services/chart-datasource/types'
import { NUM_MATCHED_ITEMS_VALUE_FIELD } from '../constants'
import { ChartJobFieldDto } from '../dto/chart-job-field.dto'
import { ProcessedDashboardJob } from './job.model'
import { FORECAST_JOB_FIELDS } from './job-lookup.model'

export const TRACKING_VALUE_FIELD_SENTINEL = 'trackingFieldValue'
export const CONTRACT_TOTAL_EXC_GST_SENTINEL = 'contractTotalExcGST'
export const CONTRACT_PRICE_SENTINEL = 'contractPrice'

export const TRACKING_VALUE_FIELD: ChartField = {
  dataField: TRACKING_VALUE_FIELD_SENTINEL,
  displayName: 'Tracking Field',
  dataType: 'number',
}

export const CONTRACT_TOTAL_EXC_GST_VALUE_FIELD: ChartField = {
  dataField: CONTRACT_TOTAL_EXC_GST_SENTINEL,
  displayName: 'Contract Total (excl. GST)',
  dataType: 'number',
  numberPrefix: '$',
}

export const CONTRACT_PRICE_VALUE_FIELD: ChartField = {
  dataField: CONTRACT_PRICE_SENTINEL,
  displayName: 'Contract Price',
  dataType: 'number',
  numberPrefix: '$',
}

/** Builds a value function for forecast tasks that looks up the numeric tracking field value per job.
 *  Returns undefined if no job fields are provided 
 *  Used by 'forecast tasks in-progress' datasource type also
 */
export function buildForecastTasksItemValueFunc(jobFields: ChartJobFieldDto[]): ItemValueFunc {
  const valueMap = new Map<string, number>()
  for (const jf of jobFields) {
    if (jf.textValue) {
      const num = Number(jf.textValue)
      if (!isNaN(num)) {
        valueMap.set(jf.jobNumber, num)
      }
    }
  }
  return (item: Record<string, unknown>, jobIdField: string) => valueMap.get(item[jobIdField] as string) ?? 0
}

export function buildJobFieldValueFunc(jobs: ProcessedDashboardJob[], field: keyof ProcessedDashboardJob): ItemValueFunc {
  const valueMap = new Map<string, number>()
  for (const job of jobs) {
    const val = job[field]
    valueMap.set(job.jobNumber, typeof val === 'number' ? val : 0)
  }
  return (item: Record<string, unknown>, jobIdField: string) => valueMap.get(item[jobIdField] as string) ?? 0
}

export const FORECAST_TASKS_FILTER_FIELDS: ChartField[] = FORECAST_JOB_FIELDS

export const FORECAST_TASKS_ARGUMENT_FIELDS: ChartField[] = [
  {
    dataField: 'forecastCompletionDate',
    displayName: 'Forecast Completion Date',
    dataType: 'date',
  },
  {
    dataField: 'forecastStartDate',
    displayName: 'Forecast Start Date',
    dataType: 'date',
  },
  {
    dataField: 'targetStartDate',
    displayName: 'Target Start Date',
    dataType: 'date',
  },
  {
    dataField: 'targetCompletionDate',
    displayName: 'Target Completion Date',
    dataType: 'date',
  },
]

export const FORECAST_TASKS_VALUE_FIELDS: ChartField[] = [NUM_MATCHED_ITEMS_VALUE_FIELD, TRACKING_VALUE_FIELD, CONTRACT_TOTAL_EXC_GST_VALUE_FIELD, CONTRACT_PRICE_VALUE_FIELD]

export const FORECAST_TASKS_DATE_FILTER_FIELDS: ChartField[] = [
  {
    dataField: 'forecastCompletionDate',
    displayName: 'Forecast Completion Date',
    dataType: 'date',
  },
  {
    dataField: 'forecastStartDate',
    displayName: 'Forecast Start Date',
    dataType: 'date',
  },
  {
    dataField: 'targetStartDate',
    displayName: 'Target Start Date',
    dataType: 'date',
  },
  {
    dataField: 'targetCompletionDate',
    displayName: 'Target Completion Date',
    dataType: 'date',
  },
]

export const FORECAST_TASKS_SERIES_GROUP_FIELDS: ChartField[] =
  FORECAST_TASKS_FILTER_FIELDS.filter(
    (jf) => jf.dataType === 'string' || jf.dataType === 'boolean'
  )
